import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, ChevronDown, Phone, Heart, Search } from 'lucide-react';
import { SiteSearchDialog } from '../search/SiteSearchDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { ProductsMegaMenu, ProductsMobileAccordion } from '../website/ProductsNav';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useSite } from '../../hooks/useSiteConfig';
import { useWishlist } from '../../contexts/WishlistContext';
import { Button } from '../ui/button';
import WishlistDrawer from '../WishlistDrawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Nav item with children: opens on label/chevron hover, chevron click or tap, and keyboard focus; Escape or outside click closes.
const NavDropdown = ({ link, navText, isHome, isScrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const slug = link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  const openedAt = useRef(0);
  const openedByClick = useRef(false);
  const open = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen((v) => { if (!v) { openedAt.current = Date.now(); openedByClick.current = false; } return true; });
  };
  // Hover/focus already opened it: the click pins it open; a click on a click-opened menu closes it
  const toggle = () => {
    if (!isOpen || !openedByClick.current || Date.now() - openedAt.current < 400) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      openedByClick.current = true;
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
  };
  const closeSoon = () => { timeoutRef.current = setTimeout(() => setIsOpen(false), 150); };

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e) => { if (!dropdownRef.current?.contains(e.target)) setIsOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [isOpen]);

  const hoverColor = isHome && !isScrolled ? 'hover:text-amber-300' : 'hover:text-amber-600';

  return (
    <div
      className="relative flex items-center"
      ref={dropdownRef}
      onMouseEnter={open}
      onMouseLeave={closeSoon}
      onFocus={open}
      onBlur={(e) => { if (!dropdownRef.current?.contains(e.relatedTarget)) setIsOpen(false); }}
    >
      <Link to={link.href} className={`text-sm font-medium ${navText} ${hoverColor} transition-colors duration-200`} data-testid={`nav-link-${slug}`}>
        {link.name}
      </Link>
      <button
        type="button"
        className={`p-1 ${navText} ${hoverColor} transition-colors duration-200`}
        onClick={toggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`${link.name} menu`}
        data-testid={`nav-dropdown-toggle-${slug}`}
      >
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className={`absolute top-full pt-2 z-50 ${link.href === '/products' ? '-left-40' : 'left-0 w-56'}`}
            data-testid={`nav-dropdown-${slug}`}
          >
            {link.href === '/products' ? <ProductsMegaMenu onNavigate={() => setIsOpen(false)} /> : <div className="bg-white rounded-md shadow-lg border border-neutral-100 py-1">
              {link.children.map((child) => (
                <Link
                  key={child.href}
                  to={child.href}
                  className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-amber-600 focus:bg-neutral-50 focus:text-amber-600 focus:outline-none transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {child.name}
                </Link>
              ))}
            </div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const toLinks = (items = []) => items.filter((i) => i.visible !== false).map((i) => ({
  name: i.label, href: i.url || '#', external: i.type === 'external',
  children: i.children?.filter((c) => c.visible !== false).length ? i.children.filter((c) => c.visible !== false).map((c) => ({ name: c.label, href: c.url || '#' })) : undefined,
}));

const NAV_LINKS = [
  { name: 'About', href: '/about' },
  { 
    name: 'Products', 
    href: '/products',
    children: [
      { name: 'Shades', href: '/products/shades' },
      { name: 'Blinds', href: '/products/blinds' },
      { name: 'Drapery', href: '/products/drapery' },
      { name: 'Fabric Swatches', href: '/fabrics' },
      { name: 'Residential', href: '/products/residential' },
      { name: 'Commercial Grade', href: '/products/commercial' },
      { name: 'Architectural', href: '/products/architectural' },
      { name: 'Motorized Systems', href: '/motorization' },
    ]
  },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Trade', href: '/trade' },
  { 
    name: 'Our Partners', 
    href: '/partners',
    children: [
      { name: 'Coulisse', href: '/partners/coulisse' },
      { name: 'Somfy', href: '/partners/somfy' },
      { name: 'Rollease Acmeda', href: '/partners/rollease-acmeda' },
      { name: 'Vertilux', href: '/partners/vertilux' },
      { name: 'Lutron', href: '/partners/lutron' },
      { name: 'Hunter Douglas', href: '/partners/hunter-douglas' },
      { name: 'Fabricut', href: '/partners/fabricut' },
    ]
  },
  { name: 'Contact', href: '/contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Ctrl/Cmd+K or "/" opens site search
  useEffect(() => {
    const onKey = (e) => {
      const typing = /input|textarea|select/i.test(e.target.tagName) || e.target.isContentEditable;
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount, setIsOpen } = useCart();
  const { wishlistCount } = useWishlist();
  const { navigation, globals } = useSite();
  const navLinks = navigation?.header?.length ? toLinks(navigation.header) : NAV_LINKS;
  const brandName = globals?.brand_name || '';
  const logoUrl = globals ? globals.logo_url : '/assets/apollo-logo.svg';
  const navPhone = globals ? globals.contact?.phone || '' : '(212) 209-0654';
  const logoMono = globals ? globals.logo_mono : true;
  const location = useLocation();

  const isHome = location.pathname === '/';
  // Transparent hero header only on the home page before scrolling, and never while the mobile menu is open.
  const transparent = isHome && !isScrolled && !mobileMenuOpen;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Floating chat/WhatsApp buttons hide while the mobile menu is open
  useEffect(() => {
    document.body.classList.toggle('fa-menu-open', mobileMenuOpen);
    return () => document.body.classList.remove('fa-menu-open');
  }, [mobileMenuOpen]);

  const navBg = transparent ? 'bg-transparent' : 'bg-white border-b border-neutral-100';
  const navText = transparent ? 'text-white' : 'text-neutral-900';

  return (
    <>
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${navBg}`} data-testid="navbar">
        <div className="container-xl">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={brandName}
                  className={logoMono ? 'h-11 md:h-16 w-auto' : `h-11 md:h-14 w-auto max-w-[200px] object-contain ${transparent ? 'bg-white/90 rounded-md p-1' : ''}`}
                  data-testid="logo-img"
                  style={transparent && logoMono ? { filter: 'brightness(0) invert(1)' } : undefined}
                />
              ) : <span className={`text-xl font-semibold ${transparent ? 'text-white' : 'text-neutral-900'}`} data-testid="logo-brand-name">{brandName}</span>}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                link.children ? (
                  <NavDropdown 
                    key={link.name} 
                    link={link} 
                    navText={navText}
                    isHome={isHome}
                    isScrolled={isScrolled}
                  />
                ) : (
                  <Link 
                    key={link.name} 
                    to={link.href} 
                    className={`text-sm font-medium ${navText} ${transparent ? 'hover:text-amber-300' : 'hover:text-amber-600'} transition-colors duration-200`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Phone Number */}
              <a 
                href={`tel:${navPhone.replace(/\D/g, '')}`} 
                className={`hidden lg:flex items-center gap-2 text-sm font-medium ${navText} ${transparent ? 'hover:text-amber-300' : 'hover:text-amber-600'} transition-colors duration-200`}
                data-testid="nav-phone"
              >
                <Phone className="w-4 h-4" />
                {navPhone}
              </a>

              {/* Wishlist */}
              <button
                onClick={() => setSearchOpen(true)}
                className={`relative p-2 ${navText} ${transparent ? 'hover:text-amber-300' : 'hover:text-amber-600'} transition-colors duration-200`}
                aria-label="Search"
                data-testid="nav-search-button"
              >
                <Search className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <SiteSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />

              <button
                onClick={() => setWishlistOpen(true)}
                className={`relative p-2 ${navText} ${transparent ? 'hover:text-amber-300' : 'hover:text-amber-600'} transition-colors duration-200`}
                data-testid="wishlist-button"
              >
                <Heart className={`w-5 h-5 ${wishlistCount > 0 ? 'fill-red-500 text-red-500' : ''}`} strokeWidth={1.5} />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </button>

              {/* Cart */}
              <button
                onClick={() => setIsOpen(true)}
                className={`relative p-2 ${navText} ${transparent ? 'hover:text-amber-300' : 'hover:text-amber-600'} transition-colors duration-200`}
                data-testid="cart-button"
              >
                <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-600 text-white text-xs rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* User Menu */}
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger className={`p-2 ${navText} ${transparent ? 'hover:text-amber-300' : 'hover:text-amber-600'} transition-colors duration-200`}>
                    <User className="w-5 h-5" strokeWidth={1.5} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard">My Dashboard</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/saved">Saved Designs</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-quotes">My Quotes</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-favorites" className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-red-500" />
                        My Favorites
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout} className="text-red-600">Sign Out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login" className={`hidden sm:block text-sm font-medium ${navText} ${transparent ? 'hover:text-amber-300' : 'hover:text-amber-600'} transition-colors duration-200`}>
                  Sign In
                </Link>
              )}

              {/* CTA Button */}
              <Link to="/quote" className="hidden lg:block">
                <Button className={transparent ? 'btn-outline-light' : 'btn-primary'} data-testid="nav-quote-btn">
                  Request Quote
                </Button>
              </Link>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`lg:hidden p-2 ${navText}`}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed inset-0 z-40 bg-white pt-20 lg:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="container-xl py-8">
              <button type="button" onClick={() => { setMobileMenuOpen(false); setSearchOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 mb-6 rounded-xl border border-neutral-200 text-neutral-500 text-sm" data-testid="mobile-search-button">
                <Search className="w-4 h-4" /> Search products, fabrics, guides…
              </button>
              <div className="flex flex-col gap-6">
                {navLinks.map((link) => (
                  <div key={link.name}>
                    <Link 
                      to={link.href} 
                      className="text-2xl font-serif text-neutral-900"
                    >
                      {link.name}
                    </Link>
                    {link.href === '/products' ? <ProductsMobileAccordion /> : link.children && (
                      <div className="pl-4 mt-3 flex flex-col gap-2">
                        {link.children.map((child) => (
                          <Link 
                            key={child.href} 
                            to={child.href}
                            className="text-neutral-600"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="mt-4 flex flex-col gap-3">
                  <a href={`tel:${navPhone.replace(/\D/g, '')}`} className="flex items-center justify-center gap-2 h-12 border border-neutral-900 text-neutral-900 font-medium" data-testid="mobile-menu-call">
                    <Phone className="w-4 h-4" /> Call {navPhone}
                  </a>
                  <Link to="/quote">
                    <Button className="btn-primary w-full" data-testid="mobile-menu-quote-btn">Request Quote</Button>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wishlist Drawer */}
      <WishlistDrawer isOpen={wishlistOpen} onClose={() => setWishlistOpen(false)} />
    </>
  );
};
